import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow, FadeUp, MaskLine, Strike } from "@/components/Reveal";

const EVOLUTION = [
  {
    date: "11.05.2024",
    title: "La première nuit",
    text: "Né d'une initiative collective, Chroma organise son premier événement à Lyon et fonde officiellement l'association — avec une direction artistique ouverte, tournée vers l'expérimentation et la rencontre.",
  },
  {
    date: "Ensuite",
    title: "La structuration",
    text: "Face aux clichés, le projet se resserre et se professionnalise : des événements mieux produits, et une approche assumée — underground, culturelle et pédagogique — qui traverse nos sélections, nos contenus et nos formats.",
  },
  {
    date: "2026",
    title: "Le déploiement",
    text: "Chroma renforce son développement par des collaborations à Lyon et à l'international, met en avant les talents latinos — DJ et producteurs basés en France — et vise des expériences toujours plus cohérentes, exigeantes et immersives pour le public lyonnais.",
  },
];

const TRAVAIL = [
  {
    index: "01",
    title: "Curation",
    text: "Une direction artistique ouverte, tournée vers l'expérimentation et la rencontre, qui croise les esthétiques et les identités de la nuit sans s'enfermer dans un genre.",
  },
  {
    index: "02",
    title: "Contenus",
    text: "Informer et documenter : mettre en récit les influences et l'héritage latino-américains pour éclairer ce qui se joue derrière la musique.",
  },
  {
    index: "03",
    title: "Sélections musicales",
    text: "De la Latin Techno au latincore, des sélections exigeantes qui approfondissent l'écoute par l'approche culturelle — et déplacent les clichés.",
  },
  {
    index: "04",
    title: "Événements",
    text: "Des formats professionnalisés, cohérents et immersifs, pensés comme des espaces de connexion au cœur de la scène lyonnaise.",
  },
];

function Hero() {
  return (
    <section className="relative overflow-hidden pt-40 md:pt-48" aria-label="Notre mission">
      <p
        className="bg-word pointer-events-none absolute -right-8 top-16 select-none font-clash text-[24vw] font-bold uppercase leading-none"
        aria-hidden="true"
      >
        Mission
      </p>
      <div className="relative mx-auto max-w-[1400px] px-5 pb-20 sm:px-8">
        <Eyebrow index="01">Notre mission</Eyebrow>
        <h1 className="mt-10 font-clash font-semibold uppercase leading-[0.95] tracking-tight">
          <MaskLine delay={0.1}>
            <span className="block text-[clamp(2.2rem,7vw,5.5rem)]">Donner du sens</span>
          </MaskLine>
          <MaskLine delay={0.22}>
            <span className="block text-[clamp(2.2rem,7vw,5.5rem)]">
              à ce qui se joue <span className="text-outline-ice">derrière</span>
            </span>
          </MaskLine>
          <MaskLine delay={0.34}>
            <span className="block text-[clamp(2.2rem,7vw,5.5rem)]">
              la musique<span className="text-glow">.</span>
            </span>
          </MaskLine>
        </h1>
        <span
          className="mt-8 inline-block rotate-[3deg] bg-glow px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-night"
          aria-hidden="true"
        >
          Depuis le 11.05.2024
        </span>
      </div>
    </section>
  );
}

function MissionBlock() {
  return (
    <section className="relative border-t border-line" aria-label="La mission">
      <span className="absolute left-4 top-4 font-mono text-ice/40" aria-hidden="true">+</span>
      <span className="absolute right-4 top-4 font-mono text-ice/40" aria-hidden="true">+</span>
      <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-24 sm:px-8 md:py-32 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Eyebrow index="02">La mission</Eyebrow>
          <h2 className="mt-8 font-clash text-2xl font-medium leading-[1.2] tracking-tight sm:text-4xl">
            <MaskLine>
              <span>
                Mettre en lumière les talents latinos — DJ et producteurs basés en France — et
                enrichir l'expérience club en replaçant le{" "}
                <span className="text-glow">contexte culturel</span> au premier plan.
              </span>
            </MaskLine>
          </h2>
        </div>
        <div className="flex flex-col justify-end lg:col-span-4 lg:col-start-9">
          <FadeUp delay={0.15}>
            <p className="text-base leading-relaxed text-muted">
              Contenus éditoriaux, sélections musicales, formats événementiels : chaque geste de
              Chroma sert le même but — mettre en avant les influences et l'héritage
              latino-américains, et donner du sens à ce qui se joue derrière la musique.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Representation() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} className="relative border-t border-line" aria-label="Représentation">
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 py-24 sm:px-8 md:py-32 lg:grid-cols-12">
        <FadeUp className="relative lg:col-span-7">
          <figure className="relative overflow-hidden border border-line">
            <motion.img
              src="/assets/crowd-blue.jpg"
              alt="Danseurs sous une lumière bleue — Chroma × Macbar, Lyon"
              style={{ y }}
              className="aspect-[4/3] w-full scale-[1.14] object-cover"
              loading="lazy"
              width="1920"
              height="1280"
            />
            <figcaption className="absolute bottom-0 right-0 bg-night/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted backdrop-blur-sm">
              Chroma × Macbar — © Laura Alvarez
            </figcaption>
          </figure>
        </FadeUp>
        <div className="lg:col-span-5">
          <Eyebrow index="03">Représentation</Eyebrow>
          <h2 className="mt-8 font-clash text-2xl font-medium leading-[1.2] tracking-tight sm:text-3xl">
            <MaskLine>
              <span>
                Faire exister les cultures latino-américaines dans l'électronique — sans les
                réduire.
              </span>
            </MaskLine>
          </h2>
          <FadeUp delay={0.15} className="mt-6">
            <p className="text-base leading-relaxed text-muted">
              Chroma explore la Latin Techno comme l'un de ses axes majeurs, sans s'y limiter, en
              croisant différentes esthétiques et identités de la nuit. Le cap reste clair :
              proposer des expériences sonores singulières, créer des espaces de rencontre et
              approfondir l'écoute par une approche culturelle, au cœur de la scène lyonnaise.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Stereotypes() {
  return (
    <section className="border-y border-line bg-aubergine" aria-label="Contre les stéréotypes">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 md:py-32">
        <Eyebrow index="04">Contre les stéréotypes</Eyebrow>
        <div className="mt-12 space-y-6 font-clash font-semibold uppercase leading-[1.08] tracking-tight">
          <MaskLine>
            <span className="block text-[clamp(1.7rem,5vw,4rem)] text-fog/90">
              <Strike>« Tu ne passes pas de reggaeton ? »</Strike>
            </span>
          </MaskLine>
          <MaskLine delay={0.15}>
            <span className="text-outline-fog block text-[clamp(1.7rem,5vw,4rem)]">
              <Strike delay={0.3}>« C'est pas vraiment latino… »</Strike>
            </span>
          </MaskLine>
        </div>
        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <FadeUp delay={0.2} className="lg:col-span-6 lg:col-start-7">
            <p className="text-base leading-relaxed text-fog/85 sm:text-lg">
              Entendus dès nos débuts, ces clichés résument exactement ce contre quoi Chroma
              existe : la culture latine ne se réduit pas à ses genres les plus mainstream. Notre
              réponse est culturelle et pédagogique — elle passe par nos sélections, nos contenus
              et nos formats, nuit après nuit.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Evolution() {
  return (
    <section className="relative" aria-label="Notre évolution">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 md:py-32">
        <Eyebrow index="05">L'évolution</Eyebrow>
        <div className="mt-12">
          {EVOLUTION.map((step, i) => (
            <FadeUp
              key={step.title}
              delay={i * 0.06}
              className="grid gap-4 border-t border-line py-10 last:border-b md:grid-cols-12 md:items-baseline md:gap-8"
            >
              <p className="font-clash text-3xl font-semibold uppercase tracking-tight text-glow md:col-span-4 lg:text-4xl">
                {step.date}
              </p>
              <h3 className="font-clash text-xl font-medium uppercase tracking-tight md:col-span-3">
                {step.title}
              </h3>
              <p className="text-base leading-relaxed text-muted md:col-span-5">{step.text}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Travail() {
  return (
    <section className="relative border-t border-line" aria-label="Le travail de Chroma">
      <p
        className="bg-word pointer-events-none absolute -left-8 bottom-10 select-none font-clash text-[20vw] font-bold uppercase leading-none"
        aria-hidden="true"
      >
        Nuit
      </p>
      <div className="relative mx-auto max-w-[1400px] px-5 py-24 sm:px-8 md:py-32">
        <Eyebrow index="06">Le travail de Chroma</Eyebrow>
        <div className="mt-12">
          {TRAVAIL.map((item, i) => (
            <FadeUp key={item.index} delay={i * 0.05}>
              <div className="group grid gap-3 border-t border-line px-2 py-9 transition-colors duration-500 last:border-b hover:bg-teal/25 md:grid-cols-12 md:items-baseline md:gap-8">
                <p className="font-mono text-xs tracking-[0.3em] text-glow md:col-span-2">
                  {item.index}
                </p>
                <h3 className="font-clash text-2xl font-semibold uppercase tracking-tight transition-colors duration-500 group-hover:text-ice md:col-span-4">
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed text-muted md:col-span-6">{item.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2} className="mt-14">
          <Link
            to="/contact"
            data-testid="mission-cta-contact"
            className="link-underline inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-glow"
          >
            Échanger avec l'équipe
            <ArrowUpRight size={15} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Mission() {
  return (
    <div data-testid="page-mission">
      <Hero />
      <MissionBlock />
      <Representation />
      <Stereotypes />
      <Evolution />
      <Travail />
    </div>
  );
}
